import React from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export type ButtonTone =
  | 'neutral'
  | 'primary'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger';
export type ButtonDensity = 'compact' | 'regular';

export interface ButtonProps extends Omit<MuiButtonProps, 'color'> {
  density?: ButtonDensity;
  tone?: ButtonTone;
}

const regularStyles = {
  minHeight: 38,
  px: 1.75,
  fontSize: '0.875rem',
  lineHeight: 1.25,
};
const compactStyles = {
  minHeight: 24,
  px: 1,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    mx: 0.5,
    '& > svg': { fontSize: '0.875rem' },
  },
};
const neutralStyles = {
  color: 'text.primary',
  bgcolor: 'action.hover',
  borderColor: 'softBorder',
  '&:hover': {
    bgcolor: 'action.selected',
    borderColor: 'outline',
  },
};
const toneColors = {
  primary: 'primary',
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'error',
} as const;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      density = 'regular',
      tone = 'primary',
      size = 'small',
      sx,
      variant,
      ...props
    },
    ref
  ) => {
    const combinedStyles = [
      density === 'compact' ? compactStyles : regularStyles,
      tone === 'neutral' ? neutralStyles : null,
      ...(Array.isArray(sx) ? sx : [sx]),
    ].filter(Boolean) as SxProps<Theme>;
    const color = tone === 'neutral' ? undefined : toneColors[tone];

    return (
      <MuiButton
        {...props}
        ref={ref}
        color={color}
        size={size}
        variant={variant || (tone === 'neutral' ? 'outlined' : 'contained')}
        sx={combinedStyles}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button };
